// src/pages/Receipts.js

import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  InputNumber,
  Tooltip,
  Input,
  Button,
  message,
  Row,
  Col,
  Select,
} from "antd";
import api from "../api/axios";
import moment from "moment";

const { Option } = Select;

// Helper function to compute receipt status
function getStatus(record, getPendingAmount, editFields) {
  const pending = getPendingAmount(record, editFields);
  if ((record.AmountReceived || 0) === 0) return "Pending";
  if (pending === 0) return "Full";
  return "Partial";
}

export default function Receipts() {
  // State
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [editFields, setEditFields] = useState({});
  const [loading, setLoading] = useState(false);

  // Filters
  const [customerFilter, setCustomerFilter] = useState([]);
  const [poNumberFilter, setPONumberFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(["Pending", "Partial"]);
  const [dateFilter, setDateFilter] = useState("");
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState([]);

  // Calculate pending amount for a receipt row
  const getPendingAmount = React.useCallback(
      (record, ef = editFields) => {
        const amountPaid = Number(record.AmountReceived) || 0;
        return Math.max(0, (record.TotalAmount || 0) - (record.TDS || 0) - amountPaid);
      },
      [editFields]  // only needs editFields if relevant, but you can put []
  );


  // Fetch receipts
  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = () => {
    setLoading(true);
    api
        .get("/receipts/sales")
        .then((res) => {
          const arr = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.data.data)
                  ? res.data.data
                  : [];
          setReceipts(arr);
          setReceipts(arr);
          setFilteredReceipts(arr);
          const fields = {};
          arr.forEach((rec) => {
            const pending = Math.max(
                0,
                (rec.TotalAmount || 0) - (rec.AmountReceived || 0) - (rec.TDS || 0)
            );
            fields[rec.InvoiceID] = {
              TDS: rec.TDS !== undefined ? rec.TDS : Math.round((rec.RateAmount || 0) * 0.1),
              Receipt: pending,
              ReceiptDate: new Date().toISOString().slice(0, 10),
              Remarks: rec.Remarks || "",
            };
          });
          setEditFields(fields);
        })
        .finally(() => setLoading(false));
  };
  ////////////////////////////
    useEffect(() => {
        if (receipts.length > 0) {
            console.log("Sample receipt:", receipts[0]);
        }
    }, [receipts]);

  // Filtering logic
  useEffect(() => {
    let data = [...receipts];
      if (invoiceNumberFilter.length)
          data = data.filter((rec) => invoiceNumberFilter.includes(rec.InvoiceNumber));
      if (customerFilter.length)
      data = data.filter((rec) => customerFilter.includes(rec.CustomerName));
    if (poNumberFilter.length)
      data = data.filter((rec) => poNumberFilter.includes(rec.PO_Number));
    if (dateFilter) {
      const m = dateFilter.match(/^([<>]=?|=)\s*(\d{4}-\d{2}-\d{2})$/);
      if (m) {
        const op = m[1];
        const dateStr = m[2];
        if (op === ">")
          data = data.filter((rec) => moment(rec.Date).isAfter(dateStr, "day"));
        else if (op === "<")
          data = data.filter((rec) => moment(rec.Date).isBefore(dateStr, "day"));
        else if (op === ">=")
          data = data.filter((rec) => moment(rec.Date).isSameOrAfter(dateStr, "day"));
        else if (op === "<=")
          data = data.filter((rec) => moment(rec.Date).isSameOrBefore(dateStr, "day"));
        else if (op === "=")
          data = data.filter((rec) => moment(rec.Date).isSame(dateStr, "day"));
      }
    }
    if (statusFilter.length)
      data = data.filter(rec =>
          statusFilter.includes(getStatus(rec, getPendingAmount, editFields))
      );
    setFilteredReceipts(data);
  }, [
    customerFilter,
    invoiceNumberFilter,
    poNumberFilter,
    dateFilter,
    statusFilter,
    receipts,
    editFields,
    getPendingAmount,
  ]);

  // Handle field changes
  const handleFieldChange = (id, field, value) => {
    setEditFields((prev) => {
      const prevRow = prev[id] || {};
      let newRow = { ...prevRow, [field]: value };
      if (field === "TDS") {
        const record = receipts.find((rec) => rec.InvoiceID === id);
        if (record) {
          const tdsEntered = Number(value) || 0;
          // Optionally, limit TDS if your business logic permits, e.g.:
           const maxTDS = Math.round((record.RateAmount || 0) * 0.1);
          const tdsSafe = Math.min(tdsEntered, maxTDS);
          const amountPaid = Number(record.AmountReceived) || 0;
          const pendingWithoutTDS = Math.max(
              0,
              (record.TotalAmount || 0) - amountPaid
          );
          // **Always recalculate Receipt = pending_without_tds - TDS entered**
          newRow.TDS = tdsEntered;
          newRow.Receipt = Math.max(0, pendingWithoutTDS - tdsEntered);
        }
      }

      if (field === "Receipt") {
        newRow.userChangedReceipt = true;
      }
      return { ...prev, [id]: newRow };
    });
  };

  // Add Receipt action
  const handleMakeReceipt = (record) => {
    const fields = editFields[record.InvoiceID] || {};
    const Receipt = Number(fields.Receipt) || 0;
    const currentTDS = Number(fields.TDS) || 0;
    const storedTDS = Number(record.TDS) || 0;
    const defaultTDS = Math.round((record.RateAmount || 0) * 0.1);

    // Only send TDS if the stored TDS is NOT already the default
    const sendTDS = (storedTDS !== defaultTDS) ? currentTDS : 0;

    const ReceiptDate = fields.ReceiptDate || new Date().toISOString().slice(0, 10);
    const Remarks = fields.Remarks || "";
    if (Receipt <= 0) {
      message.error("Enter a valid receipt amount.");
      return;
    }
    api
        .put(`/receipts/sales/${record.InvoiceID}/receipt`, {
          Receipt,
          TDS: sendTDS,
          ReceiptDate,
          Remarks,
        })
        .then(() => {
          message.success("Receipt updated!");
          fetchReceipts();
          setEditFields((prev) => ({ ...prev, [record.InvoiceID]: {} }));
        })
        .catch((e) => {
          message.error(e.response?.data?.error || "Receipt failed");
        });
  };

  // Styles
  const numStyle = { width: 60, fontSize: 13, padding: "0 4px" };
  const numEditStyle = { width: 90, fontSize: 13, padding: "0 4px" };
  const cellStyle = { width: 110, fontSize: 13, padding: "0 4px" };
  const headerStyle = { fontSize: 13, fontWeight: "bold", padding: 0 };

  // Filter options
  const customerOptions = Array.from(new Set(receipts.map((rec) => rec.CustomerName)))
      .filter(Boolean)
      .map((name) => ({ text: name, value: name }));
  const poNumberOptions = Array.from(new Set(receipts.map((rec) => rec.PO_Number)))
      .filter(Boolean)
      .map((po) => ({ text: po, value: po }));
  const statusOptions = [
    { text: "Pending", value: "Pending" },
    { text: "Partial", value: "Partial" },
    { text: "Full", value: "Full" },
  ];

  // Table columns
  const columns = [
    {
      title: <span style={headerStyle}>Invoice#</span>,
      dataIndex: "InvoiceNumber",
      sorter: (a, b) => (a.InvoiceNumber || "").localeCompare(b.InvoiceNumber || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Customer</span>,
      dataIndex: "CustomerName",
      sorter: (a, b) => (a.CustomerName || "").localeCompare(b.CustomerName || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
      {
          title: <span style={headerStyle}>Location</span>,
          dataIndex: "Location",
          sorter: (a, b) => (a.Location || "").localeCompare(b.Location || ""),
          render: (v) => <span style={cellStyle}>{v}</span>,
      },
    {
      title: <span style={headerStyle}>Date</span>,
      dataIndex: "Date",
      sorter: (a, b) => new Date(a.Date) - new Date(b.Date),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>PO#</span>,
      dataIndex: "PO_Number",
      sorter: (a, b) => (a.PO_Number || "").localeCompare(b.PO_Number || ""),
      render: (v) => <span style={cellStyle}>{v}</span>,
    },
    {
      title: <span style={headerStyle}>Rate</span>,
      dataIndex: "RateAmount",
      sorter: (a, b) => a.RateAmount - b.RateAmount,
      render: (v) => <span style={numStyle}>{v}</span>,
    },
      {
          title: <span style={headerStyle}>Tax,GST</span>,
          dataIndex: "TaxAmount",
          sorter: (a, b) => a.TaxAmount - b.TaxAmount,
          render: (v) => <span style={numStyle}>{Number(v).toFixed(2)}</span>,  // rounded to 2 decimal places
      },

    {
      title: <span style={headerStyle}>Pending</span>,
      render: (_, record) => (
          <span style={numStyle}>{getPendingAmount(record, editFields).toFixed(2)}</span>
      ),
      sorter: (a, b) => getPendingAmount(a, editFields) - getPendingAmount(b, editFields),
    },
    {
      title: <span style={headerStyle}>Status</span>,
      sorter: (a, b) =>
          getStatus(a, getPendingAmount, editFields).localeCompare(
              getStatus(b, getPendingAmount, editFields)
          ),
      render: (_, record) => {
        const status = getStatus(record, getPendingAmount, editFields);
        return (
            <span style={{ color: status !== "Full" ? "red" : "green", ...numStyle }}>
            {status}
          </span>
        );
      },
    },
    {
      title: <span style={headerStyle}>TDS Deducted</span>,
      render: (_, record) => {
        if (getStatus(record, getPendingAmount, editFields) === "Full") return null;
        const maxTDS = Math.round((record.RateAmount || 0) * 0.1);
        const currentTDS =
            editFields[record.InvoiceID]?.TDS !== undefined
                ? editFields[record.InvoiceID]?.TDS
                : Number(record.TDS) || 0;
        const isLocked = currentTDS === maxTDS;
        return (
            <Tooltip
                title={
                  isLocked
                      ? "TDS is 10% of RateAmount and locked"
                      : `Enter TDS (max ${maxTDS})`
                }
            >
              <InputNumber
                  min={0}
                  max={maxTDS}
                  value={currentTDS}
                  onChange={(val) => handleFieldChange(record.InvoiceID, "TDS", val)}
                  style={{
                    ...numEditStyle,
                    backgroundColor: isLocked ? "#f5f5f5" : undefined,
                    color: isLocked ? "#999" : undefined,
                  }}
                  disabled={isLocked}
              />
            </Tooltip>
        );
      },
      sorter: (a, b) => (Number(a.TDS) || 0) - (Number(b.TDS) || 0),
    },
    {
      title: <span style={headerStyle}>Receipt</span>,
      render: (_, record) =>
          getStatus(record, getPendingAmount, editFields) === "Full" ? null : (
              <InputNumber
                  min={0}
                  disabled={getPendingAmount(record, editFields) === 0}
                  value={Number(editFields[record.InvoiceID]?.Receipt || 0).toFixed(2)}
                  onChange={(val) =>
                      handleFieldChange(record.InvoiceID, "Receipt", val)
                  }
                  style={numEditStyle}
              />
          ),
      sorter: (a, b) => {
        const aVal =
            editFields[a.InvoiceID]?.Receipt != null
                ? editFields[a.InvoiceID].Receipt
                : getPendingAmount(a, editFields);
        const bVal =
            editFields[b.InvoiceID]?.Receipt != null
                ? editFields[b.InvoiceID].Receipt
                : getPendingAmount(b, editFields);
        return aVal - bVal;
      },
    },
    {
      title: <span style={headerStyle}>Receipt Date</span>,
      render: (_, record) =>
          getStatus(record, getPendingAmount, editFields) === "Full" ? null : (
              <Input
                  type="date"
                  value={editFields[record.InvoiceID]?.ReceiptDate}
                  onChange={(e) =>
                      handleFieldChange(record.InvoiceID, "ReceiptDate", e.target.value)
                  }
                  style={{ ...numEditStyle, minWidth: 90 }}
              />
          ),
      sorter: (a, b) => {
        const aVal = editFields[a.InvoiceID]?.ReceiptDate || "";
        const bVal = editFields[b.InvoiceID]?.ReceiptDate || "";
        return aVal.localeCompare(bVal);
      },
    },
    {
      title: <span style={headerStyle}>Remarks</span>,
      render: (_, record) =>
          getStatus(record, getPendingAmount, editFields) === "Full" ? null : (
              <Input.TextArea
                  rows={1}
                  value={editFields[record.InvoiceID]?.Remarks}
                  onChange={(e) =>
                      handleFieldChange(record.InvoiceID, "Remarks", e.target.value)
                  }
                  style={{ ...numEditStyle, height: 28, minWidth: 60, maxWidth: 110 }}
              />
          ),
    },
    {
      title: <span style={headerStyle}>Action</span>,
      render: (_, record) =>
          getStatus(record, getPendingAmount, editFields) === "Full" ? null : (
              <Button
                  type="primary"
                  disabled={getPendingAmount(record, editFields) === 0}
                  onClick={() => handleMakeReceipt(record)}
                  style={{ ...numEditStyle, height: 28 }}
              >
                Add Receipt
              </Button>
          ),
    },
  ];

  return (
      <Card title="Sales Receipts">
        {/* Filter Controls */}
        <Row gutter={8} style={{ marginBottom: 16 }}>
            <Col span={6}>
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Filter by Invoice Number"
                    value={invoiceNumberFilter}
                    onChange={setInvoiceNumberFilter}
                    allowClear
                >
                    {[...new Set(receipts.map(rec => rec.InvoiceNumber))].map(invoiceNum => (
                        <Option key={invoiceNum} value={invoiceNum}>
                            {invoiceNum}
                        </Option>
                    ))}
                </Select>
            </Col>

            <Col>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Filter by Customer"
                style={{ width: 180 }}
                onChange={setCustomerFilter}
                value={customerFilter}
                filterOption={(input, option) =>
                    option.children &&
                    option.children.toLowerCase().startsWith(input.toLowerCase())
                }
            >
              {customerOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.text}
                  </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Tooltip title="Type: >YYYY-MM-DD, <YYYY-MM-DD, >=YYYY-MM-DD, =YYYY-MM-DD">
              <Input
                  placeholder="Date (e.g. >2025-01-01)"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  style={{ width: 170 }}
              />
            </Tooltip>
          </Col>
          <Col>
            <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Filter by PO Number"
                style={{ width: 130 }}
                onChange={setPONumberFilter}
                value={poNumberFilter}
            >
              {poNumberOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.text}
                  </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
                mode="multiple"
                allowClear
                placeholder="Filter by Status"
                style={{ width: 110 }}
                onChange={setStatusFilter}
                value={statusFilter}
            >
              {statusOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.text}
                  </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Button
                onClick={() => {
                  setCustomerFilter([]);
                  setPONumberFilter([]);
                  setDateFilter("");
                  setStatusFilter(["Pending", "Partial"]);
                }}
            >
              Reset
            </Button>
          </Col>
        </Row>
        <Table
            dataSource={filteredReceipts}
            columns={columns}
            rowKey="InvoiceID"
            pagination={{ pageSize: 20 }}
            scroll={{ x: "max-content" }}
            loading={loading}
        />
      </Card>
  );
}
